import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import {
    Modal,
    Card,
    Button,
    Space,
    Tag,
    Typography,
    message,
    Alert,
    Descriptions,
    Spin,
    Popconfirm,
    Tooltip,
    Divider
} from 'antd';
import {
    SafetyCertificateOutlined,
    CopyOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    KeyOutlined
} from '@ant-design/icons';
import dnsService from '../../services/dnsService';
import DnssecRolloverPanel, { DnssecKeyTags } from '../../../shared/components/DnssecRolloverPanel';

const { Text, Paragraph } = Typography;

const DnsDnssec = ({ visible, onClose, zoneId, zoneName }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [dnssecStatus, setDnssecStatus] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchDnssecStatus = async () => {
        if (!zoneId) return;
        setLoading(true);
        try {
            const result = await dnsService.getDnssecStatus(zoneId);
            if (result.success) {
                setDnssecStatus(result.data);
            } else {
                message.error(result.error || t('Failed to retrieve DNSSEC status'));
            }
        } catch (error) {
            message.error(t('Failed to retrieve DNSSEC status: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible && zoneId) {
            fetchDnssecStatus();
        }
    }, [visible, zoneId]);

    const handleEnable = async () => {
        setActionLoading(true);
        try {
            const result = await dnsService.enableDnssec(zoneId, 'ECDSAP256SHA256');
            if (result.success) {
                message.success(t('DNSSEC enabled'));
                await fetchDnssecStatus();
            } else {
                message.error(result.error || t('Failed to enable DNSSEC'));
            }
        } catch (error) {
            message.error(t('Failed to enable DNSSEC: {{message}}', { message: error.message }));
        } finally {
            setActionLoading(false);
        }
    };

    const handleDisable = async () => {
        setActionLoading(true);
        try {
            const result = await dnsService.disableDnssec(zoneId);
            if (result.success) {
                message.success(t('DNSSEC disabled'));
                await fetchDnssecStatus();
            } else {
                message.error(result.error || t('Failed to disable DNSSEC'));
            }
        } catch (error) {
            message.error(t('Failed to disable DNSSEC: {{message}}', { message: error.message }));
        } finally {
            setActionLoading(false);
        }
    };

    const handleRegenerate = async () => {
        setActionLoading(true);
        try {
            const result = await dnsService.regenerateDnssec(zoneId, 'ECDSAP256SHA256');
            if (result.success) {
                message.success(t('ZSK rotation started; DS unchanged'));
                await fetchDnssecStatus();
            } else {
                message.error(result.error || t('Failed to rotate keys'));
            }
        } catch (error) {
            message.error(t('Failed to rotate keys: {{message}}', { message: error.message }));
        } finally {
            setActionLoading(false);
        }
    };

    const handleRollover = async (action) => {
        setActionLoading(true);
        try {
            const result = await dnsService.dnssecRollover(zoneId, action);
            if (result.success) {
                if (action === 'cancel' && result.data?.cancelled === 'ksk') {
                    message.success(t('Canceled. You can remove the new DS at your registrar.'));
                } else {
                    message.success(t('Updated'));
                }
                await fetchDnssecStatus();
            } else {
                message.error(result.error || t('Operation failed'));
            }
        } catch (error) {
            message.error(t('Operation failed: {{message}}', { message: error.message }));
        } finally {
            setActionLoading(false);
        }
    };
    const rolling = !!dnssecStatus?.rollover && dnssecStatus.rollover.phase !== 'done';

    const copyToClipboard = (text, label) => {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            message.success(t('{{label}} copied to clipboard', { label }));
        }).catch(() => {
            message.error(t('Failed to copy. Please copy manually.'));
        });
    };

    const renderDsRecords = () => {
        if (!dnssecStatus?.ds_records || dnssecStatus.ds_records.length === 0) {
            return <Text type="secondary">{t('No DS records yet')}</Text>;
        }

        return (
            <div style={{ marginTop: 16 }}>
                <Divider orientation="left" style={{ fontSize: 14 }}>
                    <KeyOutlined /> {t('DS records (configure at your registrar)')}
                </Divider>
                {dnssecStatus.ds_records.map((ds, index) => (
                    <Card
                        key={index}
                        size="small"
                        style={{ marginBottom: 12, background: '#fafafa' }}
                    >
                        <Descriptions column={1} size="small">
                            <Descriptions.Item label="Key Tag">
                                <Text code>{ds.key_tag}</Text>
                                <Tooltip title={t('Copy')}>
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<CopyOutlined />}
                                        onClick={() => copyToClipboard(String(ds.key_tag), 'Key Tag')}
                                    />
                                </Tooltip>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Algorithm')}>
                                <Text code>{ds.algorithm}</Text>
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                    ({ds.algorithm === 13 ? 'ECDSAP256SHA256' : ds.algorithm === 8 ? 'RSASHA256' : `Algorithm ${ds.algorithm}`})
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Digest type')}>
                                <Text code>{ds.digest_type}</Text>
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                    ({ds.digest_type === 2 ? 'SHA-256' : ds.digest_type === 1 ? 'SHA-1' : `Type ${ds.digest_type}`})
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Digest')}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Paragraph
                                        copyable={{ text: ds.digest }}
                                        style={{
                                            margin: 0,
                                            wordBreak: 'break-all',
                                            fontFamily: 'monospace',
                                            fontSize: 12
                                        }}
                                    >
                                        {ds.digest}
                                    </Paragraph>
                                </div>
                            </Descriptions.Item>
                        </Descriptions>
                        <Divider style={{ margin: '8px 0' }} />
                        <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>{t('Full DS record format:')}</Text>
                            <div style={{
                                background: '#f5f5f5',
                                padding: 8,
                                borderRadius: 4,
                                marginTop: 4,
                                fontFamily: 'monospace',
                                fontSize: 11,
                                wordBreak: 'break-all'
                            }}>
                                <Text code>
                                    {zoneName}. IN DS {ds.key_tag} {ds.algorithm} {ds.digest_type} {ds.digest}
                                </Text>
                                <Tooltip title={t('Copy full DS record')}>
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<CopyOutlined />}
                                        onClick={() => copyToClipboard(
                                            `${zoneName}. IN DS ${ds.key_tag} ${ds.algorithm} ${ds.digest_type} ${ds.digest}`,
                                            t('DS records')
                                        )}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    </Card>
                ))}
            </div>
        );
    };

    return (
        <Modal
            title={
                <Space>
                    <SafetyCertificateOutlined />
                    {t('DNSSEC settings')}
                    <Tag color="blue">{zoneName}</Tag>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            width={700}
            footer={[
                <Button key="close" onClick={onClose}>
                    {t('Close')}
                </Button>
            ]}
        >
            <Spin spinning={loading}>
                {dnssecStatus ? (
                    <div>
                        {/* 状态卡片 */}
                        <Card size="small" style={{ marginBottom: 16 }}>
                            <Space size="large">
                                <div>
                                    <Text type="secondary">{t('DNSSEC status')}</Text>
                                    <div style={{ marginTop: 8 }}>
                                        {dnssecStatus.enabled ? (
                                            <Tag color="success" icon={<CheckCircleOutlined />}>
                                                {t('Enabled')}
                                            </Tag>
                                        ) : (
                                            <Tag color="default" icon={<CloseCircleOutlined />}>
                                                {t('Disabled')}
                                            </Tag>
                                        )}
                                    </div>
                                </div>
                                {dnssecStatus.enabled && dnssecStatus.algorithm && (
                                    <div>
                                        <Text type="secondary">{t('Signature algorithm')}</Text>
                                        <div style={{ marginTop: 8 }}>
                                            <Tag color="blue">{dnssecStatus.algorithm}</Tag>
                                        </div>
                                    </div>
                                )}
                                {dnssecStatus.enabled && dnssecStatus.key_tag && (
                                    <div>
                                        <Text type="secondary">{t('Key')}</Text>
                                        <div style={{ marginTop: 8 }}>
                                            {Array.isArray(dnssecStatus.keys) && dnssecStatus.keys.length
                                                ? <DnssecKeyTags keys={dnssecStatus.keys} />
                                                : <Text code>{dnssecStatus.key_tag}</Text>}
                                        </div>
                                    </div>
                                )}
                            </Space>
                        </Card>

                        {/* 操作按钮 */}
                        <Space style={{ marginBottom: 16 }}>
                            {!dnssecStatus.enabled ? (
                                <Popconfirm
                                    title={t('Enable DNSSEC')}
                                    description={t('Enable DNSSEC for this zone? You will need to configure a DS record at your domain registrar.')}
                                    onConfirm={handleEnable}
                                    okText={t('OK')}
                                    cancelText={t('Cancel')}
                                >
                                    <Button
                                        type="primary"
                                        icon={<SafetyCertificateOutlined />}
                                        loading={actionLoading}
                                    >
                                        {t('Enable DNSSEC')}
                                    </Button>
                                </Popconfirm>
                            ) : (
                                <>
                                    <Popconfirm
                                        title={t('Disable DNSSEC')}
                                        description={
                                            <div>
                                                <p>{t('Disable DNSSEC?')}</p>
                                                <p style={{ color: '#ff4d4f' }}>
                                                    {t('Warning: Remove the DS record at your registrar before disabling, or your domain may fail to resolve!')}
                                                </p>
                                            </div>
                                        }
                                        onConfirm={handleDisable}
                                        okText={t('Confirm disable')}
                                        okButtonProps={{ danger: true }}
                                        cancelText={t('Cancel')}
                                        icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                                    >
                                        <Button
                                            danger
                                            loading={actionLoading}
                                        >
                                            {t('Disable DNSSEC')}
                                        </Button>
                                    </Popconfirm>
                                    <Popconfirm
                                        title={t('Rotate signing keys')}
                                        description={t('The old and new ZSKs run in parallel for about 1 hour before switching automatically. DS remains unchanged.')}
                                        onConfirm={handleRegenerate}
                                        okText={t('OK')}
                                        cancelText={t('Cancel')}
                                        disabled={rolling}
                                    >
                                        <Button
                                            icon={<ReloadOutlined />}
                                            loading={actionLoading}
                                            disabled={rolling}
                                        >
                                            {t('Rotate signing keys')}
                                        </Button>
                                    </Popconfirm>
                                    <Popconfirm
                                        title={t('Rotate KSK')}
                                        description={t('Add a new DS record at your registrar')}
                                        onConfirm={() => handleRollover('start_ksk')}
                                        okText={t('OK')}
                                        cancelText={t('Cancel')}
                                        disabled={rolling}
                                    >
                                        <Button icon={<KeyOutlined />} loading={actionLoading} disabled={rolling}>
                                            {t('Rotate KSK')}
                                        </Button>
                                    </Popconfirm>
                                </>
                            )}
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={fetchDnssecStatus}
                                loading={loading}
                            >
                                {t('Refresh status')}
                            </Button>
                        </Space>

                        {/* 是否真正生效：后端经公共解析器查父域 DS 并比对（parent_ds） */}
                        {dnssecStatus.enabled && dnssecStatus.parent_ds && (() => {
                            const pd = dnssecStatus.parent_ds;
                            const map = {
                                active: ['success', t('DNSSEC active'), t('The correct DS record is configured at your registrar. Public resolvers are validating this domain\'s signatures.')],
                                missing: ['warning', t('DNSSEC is not yet active: DS records are not configured at the registrar'), t('The zone is signed, but resolvers will only validate it once a DS record is present in the parent domain. Please add the DS record below at your domain registrar. Once it takes effect, the status here will show “Active”.')],
                                mismatch: ['error', t('DS record mismatch'), t('The DS record at your registrar does not match the current key (the key may have changed, or the domain may have been migrated from another provider). Delete the old DS record at your registrar and replace it with the DS record below. Otherwise, resolvers with DNSSEC validation enabled may fail to resolve this domain.')],
                                unvalidated: ['info', t('DS configured; awaiting resolver validation'), t('The DS record matches the current key. Public resolvers have not returned validation results yet; this usually completes within a few hours after the DS record takes effect.')],
                                unknown: ['info', t('Unable to check whether DNSSEC is active at the moment'), t('Failed to query public resolvers. Please click "Refresh status" later to try again.')],
                            };
                            const [type, title, desc] = map[pd.status] || map.unknown;
                            return (
                                <Alert
                                    type={type}
                                    showIcon
                                    style={{ marginTop: 16 }}
                                    message={title}
                                    description={
                                        <>
                                            <div>{desc}</div>
                                            <DnssecRolloverPanel status={dnssecStatus} onAction={handleRollover} loading={actionLoading} />
                                            {pd.status === 'mismatch' && Array.isArray(pd.published) && pd.published.length > 0 && (
                                                <div style={{ marginTop: 8 }}>
                                                    {t('Current DS at registrar:')}{pd.published.map((p) => `${p.key_tag} ${p.algorithm} ${p.digest_type}`).join('；')}
                                                </div>
                                            )}
                                        </>
                                    }
                                />
                            );
                        })()}

                        {/* 启用状态下显示DS记录 */}
                        {dnssecStatus.enabled && renderDsRecords()}

                        {/* 配置说明 */}
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginTop: 16 }}
                            message={t('DNSSEC setup guide')}
                            description={
                                <ol style={{ paddingLeft: 20, marginBottom: 0 }}>
                                    <li>{t('Signing keys are generated automatically when DNSSEC is enabled')}</li>
                                    <li>{t('Add the DS record shown above at your domain registrar')}</li>
                                    <li>{t('DS records usually take 24-48 hours to propagate globally')}</li>
                                    <li>{t('Delete the DS record at your registrar before disabling DNSSEC')}</li>
                                </ol>
                            }
                        />
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin />
                        <div style={{ marginTop: 16 }}>
                            <Text type="secondary">{t('Loading...')}</Text>
                        </div>
                    </div>
                )}
            </Spin>
        </Modal>
    );
};

export default DnsDnssec;
